import React, { useEffect, useState } from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import { DataElement, campaignInsightsService } from '../../services/campaignInsightsService';
import InsightsSummaryTable, { PAGE_SIZE_OPTIONS } from './InsightsSummaryTable';

interface CapsuleInsightsCardProps {
    campaignId: string | undefined;
    capsule: any;
    isExpanded: boolean;
    onToggle: () => void;
    /** The group open in the drawer, when it belongs to this capsule. */
    selectedGroupId: string | null;
    onSelectGroup: (row: DataElement, groupIdentifier: string) => void;
    /** Bumped to refetch the current page, e.g. after a review decision. */
    refreshKey: number;
}

/**
 * One capsule's output on the Insights tab: a paged summary of its groups.
 * The detail of a group opens in the page's drawer, not here.
 */
const CapsuleInsightsCard: React.FC<CapsuleInsightsCardProps> = ({
    campaignId, capsule, isExpanded, onToggle, selectedGroupId, onSelectGroup, refreshKey,
}) => {
    const [rows, setRows] = useState<DataElement[] | null>(null);
    const [total, setTotal] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isExpanded || !campaignId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        campaignInsightsService
            .getOutputSummary({ campaignId, capsuleId: capsule._id, pageNumber: page + 1, pageSize })
            .then((res) => {
                if (cancelled) return;
                // Without a reported total, "Next" from a full last page lands on an
                // empty one. Step back, and remember where the end is.
                if (res.rows.length === 0 && page > 0 && res.total === null) {
                    setTotal(page * pageSize);
                    setPage(page - 1);
                    return;
                }
                setRows(res.rows);
                if (res.total !== null) setTotal(res.total);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to fetch capsule insights:', err);
                setError('Could not load insights for this capsule.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isExpanded, campaignId, capsule._id, page, pageSize, refreshKey]);

    const changePageSize = (size: number) => {
        setPageSize(size);
        setPage(0);
        setTotal(null);
    };

    const firstLoad = loading && rows === null;

    return (
        <div className={`rounded-xl border transition-all flex flex-col ${isExpanded ? 'bg-surface/50 border-accent-fg/50 flex-1 overflow-hidden' : 'bg-surface border-line hover:border-accent-fg/30 mb-4'}`}>
            <div
                className="px-4 py-4 flex items-center justify-between cursor-pointer"
                onClick={onToggle}
            >
                <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isExpanded ? 'bg-accent text-on-accent' : 'bg-line text-accent-fg'}`}>
                        <DescriptionIcon style={{ fontSize: 20 }} />
                    </div>
                    <div>
                        <h4 className={`text-lg font-semibold ${isExpanded ? 'text-fg' : 'text-fg-body'}`}>
                            {capsule.capsuleName}
                        </h4>
                    </div>
                </div>
                <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-fg-2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </div>
            </div>

            {isExpanded && (
                <div className="border-t border-line/50 flex flex-col flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    {firstLoad ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-fg"></div>
                            <span className="ml-3 text-fg-2 text-sm">Fetching insights...</span>
                        </div>
                    ) : error && rows === null ? (
                        <div className="py-8 text-center border border-dashed border-red-500/30 rounded-xl m-6">
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    ) : rows && (rows.length > 0 || page > 0) ? (
                        <>
                            {error && (
                                <p className="text-red-300 text-xs px-4 py-2 m-0 border-b border-red-500/30 bg-red-500/10" role="alert">{error}</p>
                            )}
                            <InsightsSummaryTable
                                rows={rows}
                                total={total}
                                page={page}
                                pageSize={pageSize}
                                loading={loading}
                                selectedGroupId={selectedGroupId}
                                onSelect={onSelectGroup}
                                onPageChange={setPage}
                                onPageSizeChange={changePageSize}
                            />
                        </>
                    ) : (
                        <div className="py-8 text-center border border-dashed border-line rounded-xl m-6">
                            <p className="text-fg-muted text-sm">No insight groups found for this capsule.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CapsuleInsightsCard;
